<template>
  <Button
    variant="outline"
    size="3"
    :icon="isDark ? SunIcon : MoonIcon"
    :aria-label="isDark ? $t('modules.campaigns.layout.header.themeToLight') : $t('modules.campaigns.layout.header.themeToDark')"
    :aria-pressed="isDark"
    square
    data-testid="header-theme-toggle"
    @click="toggle"
  />
</template>

<script setup lang="ts">
import { Button, MoonIcon, SunIcon } from '@hprofits/ui';
import { computed } from 'vue';

import { useTheme } from '@/composables/useTheme';
import { useStore } from '@/composables/useVuexStore';

defineOptions({ name: 'HeaderThemeToggle' });

const { theme, setTheme } = useTheme();
const store = useStore();

/**
 * `auto` follows the system, so what the toggle flips is what is on screen right now, not the
 * stored preference: from auto-resolved dark the next click must give light, not dark again.
 */
const isDark = computed(() =>
  theme.value === 'auto'
    ? globalThis.matchMedia?.('(prefers-color-scheme: dark)').matches === true
    : theme.value === 'dark',
);

/**
 * The composable owns the DOM attributes and localStorage; the Vuex mirror is what a handful of
 * legacy components still read to pick their own dark variants (date pickers, charts, code
 * views), so both are written until that state moves out of the store.
 */
const toggle = (): void => {
  const next = isDark.value ? 'light' : 'dark';
  setTheme(next);
  store.dispatch('changeTheme', next).catch(() => undefined);
};
</script>
